import { useMemo } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Cabecalho, Capa, Carregando, EmPreparacao, NaoEncontrado, Pe } from "../componentes";
import { NumeroAnimado } from "../componentes/numero-animado";
import { ultimaMedicao } from "../dados/buscas";
import { dataCurta, mesCurto, numero, paraData } from "../dados/formatar";
import { diasSemImpulsionamento, impulsionamentosDoPeriodo } from "../dados/trabalho";
import {
  comparacoesDePosicao,
  foraDaLista,
  ligacoesAntesDepois,
  pesquisasAMenosPorMes,
  pesquisasAntesDepois,
  type ComparacaoDePosicao,
  type ComparacaoMensal,
} from "../dados/winback";
import { emPreparacao, useRelatorio } from "../hooks/use-relatorio";
import {
  Apoio,
  BarraFixa,
  Bloco,
  Card,
  Centro,
  Etiqueta,
  Faixa,
  Grade,
  Largura,
  Linha,
  Meta,
  Numero,
  Pagina,
  Pilula,
  Posicao,
  Secao,
  Titulo,
  cores,
} from "../styles";

const RETOMAR = "/georanking";

const Coluna = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 16}px;
`;

const AntesDepois = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  .ponto {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
`;

const ordinal = (posicao: number) => `${posicao}º`;

const Seta = ({ cor = cores.ink4 }: { cor?: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const rotuloDaData = (ponto: { quando: string; data: string | null }) => dataCurta(ponto.data) ?? mesCurto(ponto.quando);

const SituacaoDaPalavra = ({ comparacao }: { comparacao: ComparacaoDePosicao }) => {
  if (comparacao.situacao === "caiu") {
    if (foraDaLista(comparacao.depois.posicao)) {
      return (
        <span style={{ fontSize: 15, fontFamily: fonts.gilroyBold, color: cores.red, lineHeight: 1.3 }}>fora da lista medida</span>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Numero $tamanho={36} $cor={cores.red}>
          {ordinal(comparacao.depois.posicao)}
        </Numero>
        <Etiqueta $cor="vermelha">{`▼ ${comparacao.delta} ${comparacao.delta === 1 ? "posição" : "posições"}`}</Etiqueta>
      </div>
    );
  }
  if (comparacao.situacao === "subiu") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Numero $tamanho={36} $cor={cores.green}>
          {ordinal(comparacao.depois.posicao)}
        </Numero>
        <Etiqueta $cor="verde">{`▲ ${Math.abs(comparacao.delta)} ${Math.abs(comparacao.delta) === 1 ? "posição" : "posições"}`}</Etiqueta>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Numero $tamanho={36}>{ordinal(comparacao.depois.posicao)}</Numero>
      <Etiqueta>manteve</Etiqueta>
    </div>
  );
};

const CardMensal = ({ titulo, comparacao }: { titulo: string; comparacao: ComparacaoMensal }) => (
  <Card $fundo={cores.grayTint} style={{ display: "flex", flexDirection: "column", gap: 10, padding: 20 }}>
    <Meta style={{ fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>{titulo}</Meta>
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      <Numero $tamanho={24} $cor={cores.ink4}>
        {numero(comparacao.antes.valor)}
      </Numero>
      <Meta>{comparacao.antes.mes}</Meta>
    </div>
    <Numero $tamanho={40}>
      <NumeroAnimado valor={comparacao.depois.valor} atraso={0.3} />
    </Numero>
    {comparacao.variacao !== null && comparacao.variacao < 0 ? (
      <Etiqueta $cor="vermelha" style={{ alignSelf: "flex-start" }}>{`▼ ${Math.abs(comparacao.variacao)}% · ${comparacao.depois.mes}`}</Etiqueta>
    ) : (
      <Etiqueta style={{ alignSelf: "flex-start" }}>{comparacao.depois.mes}</Etiqueta>
    )}
  </Card>
);

export const RelatorioWinback = () => {
  const { profileId = "" } = useParams<{ profileId: string }>();
  const { perfil, pesquisas, historico, carregando, falhou } = useRelatorio(profileId, 365, { historico: true });
  const hoje = useMemo(() => new Date(), []);

  const palavras = useMemo(() => [...(perfil?.keywords ?? []), ...(perfil?.extraKeywords ?? [])], [perfil]);
  const comparacoes = useMemo(() => comparacoesDePosicao(historico), [historico]);
  const pesquisasMes = useMemo(() => pesquisasAntesDepois(pesquisas), [pesquisas]);
  const ligacoes = useMemo(() => ligacoesAntesDepois(perfil?.chartData, hoje), [perfil, hoje]);
  const aMenos = useMemo(() => pesquisasAMenosPorMes(pesquisasMes), [pesquisasMes]);
  const diasParado = useMemo(() => diasSemImpulsionamento(perfil?.posts, hoje), [perfil, hoje]);
  const pausadoDesde = useMemo(() => {
    const ultimoPost = impulsionamentosDoPeriodo(perfil?.posts)[0];
    const data = paraData(ultimoPost?.createTime) ?? ultimaMedicao(palavras);
    return data ? mesCurto(`${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`) : null;
  }, [perfil, palavras]);

  if (carregando) {
    return (
      <Pagina>
        <Cabecalho />
        <Carregando />
      </Pagina>
    );
  }
  if (falhou || !perfil) {
    return (
      <Pagina>
        <Cabecalho />
        <NaoEncontrado />
      </Pagina>
    );
  }
  if (emPreparacao(perfil)) {
    return (
      <Pagina>
        <Cabecalho />
        <Capa perfil={perfil} />
        <EmPreparacao />
      </Pagina>
    );
  }

  const maisRecente = [...comparacoes].sort((a, b) => (b.depois.data ?? b.depois.quando).localeCompare(a.depois.data ?? a.depois.quando))[0];
  const novaMedicao = maisRecente ? rotuloDaData(maisRecente.depois) : dataCurta(ultimaMedicao(palavras));
  const consultadoEm = dataCurta(hoje);
  const temHistorico = comparacoes.length > 0;
  const temNumeros = pesquisasMes !== null || ligacoes !== null || diasParado !== null;

  return (
    <Pagina>
      <Cabecalho acao={{ href: RETOMAR, rotulo: "Retomar meu plano" }} />
      <Capa
        perfil={perfil}
        etiqueta={pausadoDesde ? <Etiqueta $cor="amarela">{`Pausado desde ${pausadoDesde}`}</Etiqueta> : undefined}
      />

      <Largura>
        <Secao $atraso={0.2} style={{ paddingTop: 28, paddingBottom: 28 }}>
          <Centro>
            <h1>Seu histórico continua registrado</h1>
            <p className="frase" style={{ fontSize: 16, lineHeight: 1.6 }}>
              {temHistorico ? (
                <>
                  Atualizamos{" "}
                  <strong style={{ color: cores.ink, fontFamily: fonts.gilroyBold }}>
                    {`${comparacoes.length} ${comparacoes.length === 1 ? "busca importante" : "buscas importantes"}`}
                  </strong>{" "}
                  para mostrar o que mudou desde a pausa. Cada número tem data e vem lado a lado.
                </>
              ) : (
                "As medições feitas durante o acompanhamento seguem guardadas. Assim que houver uma nova medição, o antes e o depois aparecem aqui, lado a lado."
              )}
            </p>
            <Pilula href={RETOMAR} style={{ width: "100%", maxWidth: 360 }}>
              Retomar meu plano
            </Pilula>
            {novaMedicao && <Meta>{`Medições GeoRanking · nova medição em ${novaMedicao}`}</Meta>}
          </Centro>
        </Secao>
      </Largura>

      {temHistorico && (
        <>
          <Largura>
            <Secao $atraso={0.3} style={{ paddingTop: 0, paddingBottom: 8 }}>
              <Bloco style={{ gridTemplateColumns: "1fr" }}>
                <Card style={{ display: "flex", flexDirection: "column", padding: "8px 22px" }}>
                  <div style={{ padding: "14px 0 6px" }}>
                    <Meta style={{ fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>O que você tinha conquistado</Meta>
                  </div>
                  {comparacoes.map((comparacao) => (
                    <Linha key={comparacao.id}>
                      <Posicao $topo={comparacao.antes.posicao <= 3}>{ordinal(comparacao.antes.posicao)}</Posicao>
                      <div className="texto">
                        <span className="nome">{comparacao.titulo}</span>
                        <Meta>{`Google Maps · medida em ${rotuloDaData(comparacao.antes)}`}</Meta>
                      </div>
                    </Linha>
                  ))}
                </Card>
              </Bloco>
            </Secao>
          </Largura>

          <Largura>
            <Secao $atraso={0.45}>
              <Coluna $gap={18}>
                <Coluna $gap={6}>
                  <Titulo>Como está agora</Titulo>
                  <Apoio>{`Medimos de novo em ${novaMedicao}, nas mesmas condições da medição anterior.`}</Apoio>
                </Coluna>
                <Grade $colunas={1} $desktop={3}>
                  {comparacoes.map((comparacao) => (
                    <Card key={comparacao.id} $fundo={cores.grayTint} style={{ display: "flex", flexDirection: "column", gap: 12, padding: 20 }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, lineHeight: 1.35, overflowWrap: "anywhere" }}>{comparacao.titulo}</span>
                      <Meta>Google Maps</Meta>
                      <AntesDepois>
                        <div className="ponto">
                          <Meta>{rotuloDaData(comparacao.antes)}</Meta>
                          <Numero $tamanho={36} $cor={cores.ink4}>
                            {ordinal(comparacao.antes.posicao)}
                          </Numero>
                        </div>
                        <Seta />
                        <div className="ponto">
                          <Meta>{rotuloDaData(comparacao.depois)}</Meta>
                          <SituacaoDaPalavra comparacao={comparacao} />
                        </div>
                      </AntesDepois>
                    </Card>
                  ))}
                </Grade>
              </Coluna>
            </Secao>
          </Largura>
        </>
      )}

      {temNumeros && (
        <Largura>
          <Secao $atraso={0.6} style={{ paddingTop: temHistorico ? 0 : undefined }}>
            <Coluna $gap={18}>
              <Coluna $gap={6}>
                <Titulo>Pesquisas e contatos</Titulo>
                <Apoio>Dados do Google, antes e depois da pausa.</Apoio>
              </Coluna>
              <Grade $colunas={pesquisasMes && ligacoes ? 2 : 1} $desktop={3}>
                {pesquisasMes && <CardMensal titulo="Pesquisas no mês" comparacao={pesquisasMes} />}
                {ligacoes && <CardMensal titulo="Ligações" comparacao={ligacoes} />}
                {diasParado !== null && (
                  <Card $fundo={cores.grayTint} style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px" }}>
                    <Numero $tamanho={40}>
                      <NumeroAnimado valor={diasParado} atraso={0.3} />
                    </Numero>
                    <Coluna $gap={2}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15 }}>{diasParado === 1 ? "dia sem impulsionamento novo" : "dias sem impulsionamento novo"}</span>
                      <Meta>{`último em ${dataCurta(impulsionamentosDoPeriodo(perfil.posts)[0]?.createTime) ?? ""}`}</Meta>
                    </Coluna>
                  </Card>
                )}
              </Grade>
              <Meta>Dados do Google · disponíveis enquanto houver acesso ao perfil.</Meta>
            </Coluna>
          </Secao>
        </Largura>
      )}

      {!temHistorico && !temNumeros && (
        <Largura>
          <Secao>
            <Card $fundo={cores.yellowTint} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 15, fontFamily: fonts.gilroySemiBold, color: cores.amber }}>Ainda sem dados comparáveis</span>
              <Apoio style={{ margin: 0, fontSize: 13 }}>Este perfil não tem duas medições em meses diferentes nem dados do Google no período. Nada foi perdido.</Apoio>
            </Card>
          </Secao>
        </Largura>
      )}

      {aMenos !== null && pesquisasMes && (
        <Faixa $cor="escura" $atraso={0.75} style={{ paddingBottom: 100 }}>
          <Largura>
            <Coluna $gap={18}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <Coluna $gap={6}>
                  <Titulo style={{ color: "#ffffff" }}>O que pode estar ficando na mesa</Titulo>
                  <Apoio $claro>Sem o acompanhamento, menos pesquisas no Google chegaram até a sua empresa.</Apoio>
                </Coluna>
                <Etiqueta $cor="clara" style={{ flexShrink: 0 }}>
                  Dados do Google
                </Etiqueta>
              </div>
              <Card style={{ display: "flex", flexDirection: "column", gap: 6, padding: 22 }}>
                <Meta style={{ fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>Pesquisas a menos por mês</Meta>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <Numero $tamanho={52} $cor={cores.red}>
                    <NumeroAnimado valor={aMenos} atraso={0.3} />
                  </Numero>
                  <span style={{ fontSize: 15, color: cores.ink2 }}>pesquisas que deixaram de mostrar sua empresa</span>
                </div>
              </Card>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 18, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
                <Meta style={{ color: "#ffffff", fontFamily: fonts.gilroySemiBold }}>Como calculamos</Meta>
                <Meta $claro>
                  {`Pesquisas por mês que mostraram sua empresa: ${pesquisasMes.antes.mes} (${numero(pesquisasMes.antes.valor)}) menos ${pesquisasMes.depois.mes} (${numero(pesquisasMes.depois.valor)}). São dados do próprio Google, não uma estimativa.`}
                </Meta>
              </div>
              <Pilula $tipo="clara" href={RETOMAR} style={{ width: "100%", marginTop: 4 }}>
                Retomar meu plano
              </Pilula>
            </Coluna>
          </Largura>
        </Faixa>
      )}

      <Pe texto={`Relatório GeoRanking · Dados do Google e medições GeoRanking · ${consultadoEm}`} />
      <BarraFixa>
        <Pilula href={RETOMAR} style={{ width: "100%" }}>
          Retomar meu plano
        </Pilula>
      </BarraFixa>
    </Pagina>
  );
};
